import i18n from '@/utils/i18n';
import { createStyles } from '@/contexts/theme-context';
import { type FC } from 'react';
import {
  getLocalizedDateString,
  getOrderSourceContact,
} from '@/screens/order-inbounds/utils';
import { LIST_ITEM_PADDING, TYPOGRAPHY } from '@/styles';
import { type OrderMetadata } from '@/screens/order-inbounds/types';
import { Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

interface InboundDetailHeaderMetadataPopoverContentProps {
  orders: OrderMetadata[];
}

export const InboundDetailHeaderMetadataPopoverContent: FC<
  InboundDetailHeaderMetadataPopoverContentProps
> = ({ orders }) => {
  const [styles] = useStyles();

  if (orders.length === 0) {
    return null;
  }

  const names =
    orders[0].uniqueEmployeeNames.length > 0
      ? orders[0].uniqueEmployeeNames.join(', ')
      : null;

  const isMergedOrder = Boolean(orders[0].mergeUUID);

  const isEmail = orders[0].subject || orders[0].source === 'EMAIL';

  const contactTestIdSuffix = isMergedOrder
    ? '-merged'
    : isEmail
      ? '-email'
      : '-phone';

  return (
    <View
      style={styles.popoverContent}
      testID="original-inbound-metadata-popover-content"
    >
      {orders.map((order) => (
        <View key={order.smartOrderUUID} style={styles.popoverContact}>
          <View style={[styles.line, styles.lineContact]}>
            {order.employee?.name ? (
              <Text
                style={[TYPOGRAPHY.h4SemiBold, styles.textPrimary]}
                testID={`original-inbound-line${names ? '-name' : ''}${contactTestIdSuffix}`}
              >
                {order.employee?.name}
                {getOrderSourceContact(order)
                  ? ` • ${getOrderSourceContact(order)}`
                  : null}
              </Text>
            ) : null}
          </View>

          <View style={[styles.line, styles.lineDates]}>
            {Boolean(order.originalSentAt) ? (
              <Typography
                flexShrink={0}
                testID="original-inbound-line-original-sent-time"
                variant="paragraph-md"
              >
                {i18n.t('sent_with_date', {
                  date: getLocalizedDateString(order.originalSentAt!),
                })}
              </Typography>
            ) : null}

            <Typography
              flexShrink={0}
              testID="original-inbound-line-received-time"
              variant="paragraph-md"
            >
              {i18n.t('received_with_date', {
                date: getLocalizedDateString(order.createdAt),
              })}
            </Typography>
          </View>
        </View>
      ))}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  line: {
    flexDirection: 'row',
  },
  lineContact: {
    gap: 8,
    justifyContent: 'space-between',
  },
  lineDates: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  popoverContact: {
    gap: 4,
  },
  popoverContent: {
    gap: LIST_ITEM_PADDING,
  },
  textPrimary: {
    color: theme.sk.color.text.primary,
  },
}));
